/* eslint-disable no-shadow */
/* eslint-disable no-unused-vars */
/* eslint-disable react/button-has-type */
/* eslint-disable react/jsx-no-duplicate-props */
import React from 'react';
import { injectStripe } from 'react-stripe-elements';
import { connect } from 'react-redux';
import Moment from 'moment';
import PropTypes from 'prop-types';

import CardSection from '../CardSection';
import CvcSection from '../CvcSection';
import ExpirySection from '../ExpirySection';
import CouponCodeSection from '../CouponCodeSection';
import TaPricingNotification from '../../TaPricingNotification';
import withStatsig from '../../hocs/withStatsig';
import paymentsConstants from '../../../../constants/payments';
import { getClickAttrs } from '../../Analytics/gtag';

import styles from '../../Form/themeV3.scss';
import { showErrorPopup } from '../../../actions/notification-actions';
import { clearCardError } from '../../../actions/checkout-subscription-actions';
import { interpretStripeCardError, getStripeValidationErrorField } from '../../../utils/payments';

const { paymentFormTypes } = paymentsConstants;

export class CheckoutForm extends React.Component {
    constructor() {
        super();
        this.state = {
            submitting: false,
            error: {
                name: null,
                cardNumber: null,
                cardCvc: null,
                cardExpiry: null,
            },
        };
        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleChange = this.handleChange.bind(this);
        this.handleNameChange = this.handleNameChange.bind(this);
        this.handleCardElementReady = this.handleCardElementReady.bind(this);
        this.cardNumberRef = null;
    }

    componentDidMount() {
        if (this.props.cardError) {
            this.props.clearCardError();
        }
    }

    handleCardElementReady(element) {
        this.cardNumberRef = element;
    }

    async handleSubmit(ev) {
        const { onSuccess, stripe, showCardErrorPopup, onCardError, onSubmitStart, onSubmitAborted } = this.props;
        ev.preventDefault();
        if (onSubmitStart) {
            onSubmitStart();
        }
        // Within the context of `Elements`, this call to createToken knows which Element to
        // tokenize, since there's only one in this group.
        const { cardholderName, error } = this.state;
        error.name = !cardholderName ? 'You must provide a name' : null;
        this.setState({ error, submitting: true });
        // Tracks whether the attempt actually handed off to the API. Incomplete card fields
        // never get that far, and a host that flipped its own in-flight flag on
        // onSubmitStart has nothing else to tell it the attempt is over — the payment
        // request it is waiting on was never made, so no response will arrive to clear it.
        let reachedApi = false;
        try {
            const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
                type: 'card',
                card: this.cardNumberRef,
                billing_details: {
                    name: cardholderName,
                },
            });

            if (stripeError) {
                const message = interpretStripeCardError(stripeError);
                const validationField = getStripeValidationErrorField(stripeError);
                if (validationField) {
                    // Nothing reached Stripe, so there is no failure to announce — only a field to
                    // finish. A popup here would be dismissable, and dismissing it tears down the
                    // host panel along with the selection behind it.
                    error[validationField] = message;
                    this.setState({ error });
                } else if (onCardError) {
                    // Hosts that render card errors in place opt out of the app-wide popup.
                    onCardError(message);
                } else {
                    showCardErrorPopup({
                        message: 'Unable to process request',
                        error: message,
                    });
                }
            } else {
                reachedApi = true;
                onSuccess(paymentMethod.id);
            }

            if (this.props?.onFormSubmit) {
                this.props.onFormSubmit();
            }
        } finally {
            this.setState({ submitting: false });
            if (!reachedApi && onSubmitAborted) {
                onSubmitAborted();
            }
        }
    }

    handleChange(ev) {
        if (ev.elementType) {
            const { error } = this.state;
            if (ev.error) {
                error[ev.elementType] = ev.error.message;
                this.setState({ error });
            } else {
                error[ev.elementType] = null;
                this.setState({ error });
            }
        }
    }

    handleNameChange(ev) {
        const { cardholderName, error } = this.state;
        const newName = ev.target.value;
        if (cardholderName && !newName) {
            error.name = 'You must provide a name';
        } else {
            error.name = null;
        }
        this.setState({ cardholderName: newName, error });
    }

    submitButtonAndBillingDetails = () => {
        const { submitting, error } = this.state;
        const { disabled, trialAvailability, formType, invoiceItems, buttonText } = this.props;
        const hasFormErrors = error && (error.name || error.cardNumber || error.cardCvc || error.cardExpiry);
        return (
            <div>
                <button
                    className={`btn btn-xl btn-primary text-uppercase font-weight-normal w-100 ${(submitting || this.props.submitting) ? 'text-dark' : ''}`}
                    disabled={submitting || disabled || this.props.submitting || hasFormErrors}
                    data-testid="checkout.submit-btn"
                    {...getClickAttrs({
                        category: 'Checkout Started',
                        context: `${formType} Form Checkout`,
                        location: 'Checkout Form CTA',
                    })}
                >
                    {(submitting || this.props.submitting) && (<i className="fas fa-circle-notch fa-spin mr8" />)}
                    {buttonText}
                </button>

                {trialAvailability.data.endDate && formType === paymentFormTypes.powered && invoiceItems && (
                    <div className={'text-center mt16'} data-testid="checkout-form.trial-period-info-powered">
                        You will not be charged during your trial period. You will be billed {invoiceItems.total.currency}{invoiceItems.total.amount.toFixed(2)} on {Moment(trialAvailability.data.endDate).format('MMMM Do, YYYY')}. You may cancel at anytime.
                    </div>
                )}

                {trialAvailability.data.endDate && this.props.formType === 'pro_club_college' && this.props.lineItem !== null && (
                    <div className={'text-center mt16'} data-testid="checkout-form.trial-period-info-pro-club">
                        You will not be charged during your trial period. You will be billed {this.props.invoiceItems.total.currency}{this.props.invoiceItems.total.amount.toFixed(2)} on {Moment(trialAvailability.data.endDate).format('MMMM Do, YYYY')}. You may cancel at anytime.
                    </div>
                )}
            </div>
        );
    };

    render() {
        const { cardholderName, error } = this.state;
        const nameId = 'cardholder-name';
        const {
            lineItem,
            cardError,
            themeV3,
            hideCardError,
            checkGate,
            featureGates,
        } = this.props;

        const referralsEnabled = checkGate(featureGates.REFERRAL_SYSTEM);
        const couponsEnabled = checkGate(featureGates.POWER_COUPON_CODES);

        // only way to fix font
        const stripeInputStyle = {
            base: {
                fontFamily: '\'Inter\' , Arial, sans-serif',
                fontSize: '16px',
                fontWeight: 500,
                color: '#2b3339',
                '::placeholder': {
                    fontSize: '14px',
                    color: '#b9c5cd',
                },
            },
        };

        return (
            <form onSubmit={this.handleSubmit} id="checkOutForm" data-testid="checkout-form">
                <div className={`form-group form-group-xl  ${error.name && 'is-invalid'} ${themeV3 ? styles.themeV3 : ''}`}>
                    <label htmlFor={nameId} data-testid="checkout-form.cardholder-name-label">
                        {!themeV3 && 'Cardholder Name'}
                    </label>
                    <div className={`${styles.inputWrapper} input-wrapper`}>
                        <input
                            id={nameId}
                            className={`${styles.formControl} form-control`}
                            value={cardholderName}
                            onChange={this.handleNameChange}
                            data-testid="checkout-form.cardholder-name"
                        />
                        {themeV3 && <label data-testid="checkout-form.cardholder-name-label-v3">Cardholder Name</label>}
                        <div className="error-indicator" />
                    </div>
                    {error.name && <div className="invalid-feedback">{error.name}</div>}
                </div>
                <div className="row">
                    <CardSection
                        error={error.cardNumber}
                        onChange={this.handleChange}
                        style={stripeInputStyle}
                        onCardElementReady={this.handleCardElementReady}
                        themeV3={themeV3}
                    />
                </div>
                <div className="row">
                    <CvcSection
                        error={error.cardCvc}
                        onChange={this.handleChange}
                        style={stripeInputStyle}
                        themeV3={themeV3}
                    />
                    <ExpirySection
                        error={error.cardExpiry}
                        onChange={this.handleChange}
                        style={stripeInputStyle}
                        themeV3={themeV3}
                    />
                </div>
                {this.props.renderCardBrands?.()}
                {this.props.isTennisAustraliaMember && (
                    <TaPricingNotification savingsPercentage={this.props.savingsPercentage} />
                )}
                {(couponsEnabled) && (
                    <CouponCodeSection
                        onChange={this.handleChange}
                        style={stripeInputStyle}
                        error={this.props.couponError}
                        couponCode={this.props.couponCode}
                        applyCoupon={this.props.applyCoupon}
                    />
                )}
                {!hideCardError && (
                    <div className={`${cardError && 'is-invalid'}`}>
                        {cardError && <div className="invalid-feedback">{cardError}</div>}
                    </div>
                )}
                {this.props.renderBeforeLineItem?.()}
                {lineItem}
                {this.props.renderv3powerpricing?.()}
                {this.submitButtonAndBillingDetails()}
            </form>
        );
    }
}

CheckoutForm.propTypes = {
    stripe: PropTypes.shape({
        createToken: PropTypes.func.isRequired,
        paymentRequest: PropTypes.func,
    }),
    onSuccess: PropTypes.func.isRequired,
    showCardErrorPopup: PropTypes.func,
    onCardError: PropTypes.func,
    formType: PropTypes.string.isRequired,
    buttonText: PropTypes.string.isRequired,
    disabled: PropTypes.bool,
    submitting: PropTypes.bool,
    cardError: PropTypes.string,
    isUsingStripeForm: PropTypes.bool,
    invoiceItems: PropTypes.shape({
        total: PropTypes.shape({
            amount: PropTypes.number,
            currency: PropTypes.string,
        }),
        lineItems: PropTypes.arrayOf(PropTypes.shape({
            id: PropTypes.string,
            amount: PropTypes.number,
            description: PropTypes.string,
        })),
    }),
    featureGates: PropTypes.shape({
        REFERRAL_SYSTEM: PropTypes.string,
        POWER_COUPON_CODES: PropTypes.string,
    }).isRequired,
    checkGate: PropTypes.func.isRequired,
    setUsingStripeForm: PropTypes.func,
    setIsPaymentByDefaultCard: PropTypes.func,
    trialAvailability: PropTypes.shape({
        data: PropTypes.shape({
            endDate: PropTypes.string,
        }),
    }),
    lineItem: PropTypes.node,
    couponError: PropTypes.string,
    couponCode: PropTypes.string,
    applyCoupon: PropTypes.func,
    hideCardError: PropTypes.bool,
    themeV3: PropTypes.bool,
    renderv3powerpricing: PropTypes.func,
    renderBeforeLineItem: PropTypes.func,
    isTennisAustraliaMember: PropTypes.bool,
    savingsPercentage: PropTypes.number,
    onFormSubmit: PropTypes.func,
    onSubmitStart: PropTypes.func,
    onSubmitAborted: PropTypes.func,
    renderCardBrands: PropTypes.func,
};

CheckoutForm.defaultProps = {
    showCardErrorPopup: () => { },
    onCardError: null,
    disabled: false,
    submitting: false,
    cardError: null,
    isUsingStripeForm: false,
    hideCardError: false,
    themeV3: false,
    renderv3powerpricing: null,
    isTennisAustraliaMember: false,
    savingsPercentage: 0,
    stripe: null,
    invoiceItems: {
        total: {
            amount: 0,
            currency: 'USD',
        },
        lineItems: [],
    },
    setUsingStripeForm: () => { },
    setIsPaymentByDefaultCard: () => { },
    trialAvailability: {
        data: {
            endDate: null,
        },
    },
    lineItem: null,
    couponError: null,
    couponCode: '',
    applyCoupon: () => { },
    onFormSubmit: null,
    onSubmitStart: null,
    onSubmitAborted: null,
    renderCardBrands: null,
    renderBeforeLineItem: null,
};

function mapDispatchToProps(dispatch) {
    return {
        clearCardError: () => dispatch(clearCardError()),
        // This used to read `dispatch` straight off props, which connect never supplies
        // once mapDispatchToProps is given — so every Stripe card error threw instead of
        // showing the popup, and the throw escaped handleSubmit as an unhandled rejection.
        showCardErrorPopup: payload => dispatch(showErrorPopup(payload)),
    };
}

export default injectStripe(withStatsig(connect(null, mapDispatchToProps)(CheckoutForm)));
